import { injectable } from 'inversify'
import type { UniqueKeys } from '@/types'

type PromiseExecutorPoolItem = {
  keys: UniqueKeys
  promise: Promise<unknown>
  count: number
}

@injectable()
export class PromiseExecutorPoolManager {
  protected pool: Array<PromiseExecutorPoolItem> = []

  public hasPromiseInPool(keys: UniqueKeys) {
    return !!this.getItem(keys)
  }

  public getPromiseFromPool(keys: UniqueKeys) {
    return this.getItem(keys)?.promise
  }

  public getPromiseCountInPool(keys: UniqueKeys) {
    return this.getItem(keys)?.count ?? 0
  }

  public addPromiseToPool(keys: UniqueKeys, promise: Promise<unknown>) {
    const index = this.getItemIndex(keys)
    if (index !== -1) {
      this.pool[index].count += 1
    } else {
      this.pool.push({ keys, promise, count: 1 })
    }
  }

  public removePromiseFromPool(keys: UniqueKeys) {
    const index = this.getItemIndex(keys)
    if (index === -1) {
      return
    }
    if (this.pool[index].count === 1) {
      this.pool.splice(index, 1)
    } else {
      this.pool[index].count -= 1
    }
  }

  public cleanup() {
    this.pool = []
  }

  protected isMatchedByKeys(keys: UniqueKeys, item: PromiseExecutorPoolItem) {
    return keys.every((key) => item.keys.includes(key))
  }

  protected getItem(keys: UniqueKeys) {
    return this.pool.find((item) => this.isMatchedByKeys(keys, item))
  }

  protected getItemIndex(keys: UniqueKeys) {
    return this.pool.findIndex((item) => this.isMatchedByKeys(keys, item))
  }
}
